import React, { useState, useEffect } from 'react';
import { getDashboardStats } from '../../services/dashboardService';
import Loader from '../../components/common/Loader';
import Button from '../../components/common/Button';
import { useNavigate } from 'react-router-dom';
import { 
  CubeTransparentIcon, 
  MapIcon, 
  QuestionMarkCircleIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

function DashboardPage() {
  const [dashboardData, setDashboardData] = useState({
    overview: {
      published_packages_count: 0,
      total_packages_count: 0,
      destinations_count: 0,
      sub_destinations_count: 0,
      faqs_count: 0,
      testimonials_count: 0,
      media_assets_count: 0,
      active_admin_count: 0,
      google_reviews_count: 0,
      average_rating: 0,
      featured_packages_count: 0
    },
    recent_activity: [],
    package_status_distribution: [],
    top_destinations: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getDashboardStats();
        setDashboardData(data);
      } catch (err) {
        setError('Failed to load dashboard data: ' + err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-2 bg-gray-50 min-h-screen">
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl relative mx-2 shadow-lg" role="alert">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
              <span className="block sm:inline font-medium">{error}</span>
            </div>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-4 py-2 rounded-lg text-sm transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { overview } = dashboardData;

  const StatCard = ({ icon: Icon, title, value, change = null, changeType = null, gradientFrom = 'from-indigo-400', gradientTo = 'to-indigo-500' }) => (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 flex flex-col justify-between h-36 sm:h-40 border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] hover:-translate-y-1">
      <div className="flex justify-between items-start mb-3">
        <p className="text-sm font-semibold text-gray-700">{title}</p>
        <div className={`p-2 rounded-xl bg-gradient-to-br ${gradientFrom} ${gradientTo} text-white shadow-lg`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="text-3xl font-extrabold text-gray-900 mb-2">{value}</div>
      {change !== null && (
        <div className={`flex items-center text-sm font-semibold ${changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
          {changeType === 'increase' ? (
            <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
          ) : (
            <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
          )}
          {change}%
        </div>
      )}
    </div>
  );

  return (
    <div className="p-2 bg-gray-50 min-h-screen">
      <div className="w-full">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center px-2">
          <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
          Analytics Overview
        </h2>
        
        {/* Statistics Cards - Real data from backend */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
              Core Statistics
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <StatCard 
                icon={CubeTransparentIcon} 
                title="Total Packages" 
                value={overview.total_packages_count} 
                gradientFrom="from-blue-500" 
                gradientTo="to-blue-600" 
              />
              <StatCard 
                icon={CubeTransparentIcon} 
                title="Published Packages" 
                value={overview.published_packages_count} 
                gradientFrom="from-indigo-500" 
                gradientTo="to-indigo-600" 
              />
              <StatCard 
                icon={MapIcon} 
                title="Destinations" 
                value={overview.destinations_count} 
                gradientFrom="from-green-500" 
                gradientTo="to-green-600" 
              />
              <StatCard 
                icon={ChatBubbleLeftRightIcon} 
                title="Testimonials" 
                value={overview.testimonials_count} 
                gradientFrom="from-purple-500" 
                gradientTo="to-purple-600" 
              />
              <StatCard 
                icon={QuestionMarkCircleIcon}
                title="FAQ Entries"
                value={overview.faqs_count}
                gradientFrom="from-amber-500" 
                gradientTo="to-orange-500" 
              />
            </div>
          </div>

          {/* Additional Statistics Row */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              Additional Metrics
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard 
                icon={PhotoIcon} 
                title="Media Assets" 
                value={overview.media_assets_count} 
                gradientFrom="from-teal-500" 
                gradientTo="to-teal-600" 
              />
              <StatCard 
                icon={UserGroupIcon} 
                title="Active Admins" 
                value={overview.active_admin_count} 
                gradientFrom="from-rose-500" 
                gradientTo="to-rose-600" 
              />
              <StatCard 
                icon={CubeTransparentIcon} 
                title="Featured Packages" 
                value={overview.featured_packages_count} 
                gradientFrom="from-violet-500" 
                gradientTo="to-violet-600" 
              />
              <StatCard 
                icon={ChatBubbleLeftRightIcon} 
                title="Avg Rating" 
                value={overview.average_rating ? `${overview.average_rating.toFixed(1)}/5` : 'N/A'} 
                gradientFrom="from-emerald-500" 
                gradientTo="to-emerald-600" 
              />
            </div>
          </div>
        </div>

        {/* Recent Activity Section */}
        {dashboardData.recent_activity && dashboardData.recent_activity.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                Recent Activity
              </h3>
              <div className="space-y-3">
                {dashboardData.recent_activity.slice(0, 5).map((activity, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white hover:bg-purple-50 rounded-xl transition-colors duration-200 border border-purple-200 shadow-sm">
                    <div className="flex items-center space-x-4">
                      <div className={`w-3 h-3 rounded-full shadow-sm ${
                        activity.type === 'package' ? 'bg-blue-500' :
                        activity.type === 'testimonial' ? 'bg-green-500' :
                        'bg-purple-500'
                      }`}></div>
                      <span className="text-sm font-semibold text-gray-900">{activity.name}</span>
                      <span className="text-xs text-gray-600 capitalize bg-gray-100 px-2 py-1 rounded-lg">{activity.action}</span>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">
                      {new Date(activity.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 hover:shadow-xl transition-shadow duration-300 mx-2">
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <div className="w-3 h-3 bg-amber-500 rounded-full mr-3"></div>
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                onClick={() => navigate('/packages/new')}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Add New Package
              </Button>
              <Button
                onClick={() => navigate('/locations')}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Manage Locations
              </Button>
              <Button
                onClick={() => navigate('/testimonials')}
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white py-3 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                Review Testimonials
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
